/**
 * React hook wrapping the ``GET /orders/{orderId}/editability`` check for
 * ``inflection_test`` (LSMC) orders.
 *
 * Product intent: for ``inflection_test`` orgs the SPA asks provider-api (which
 * proxies mc-api, the source of truth) whether the order may still be edited.
 * When the answer is ``true`` every patient + comparator section is editable and
 * "Send Updates" is enabled; when ``false`` they are locked. ``rcigm`` orders do
 * not use this gate — the hook returns ``null`` ("not applicable") so callers
 * fall back to the existing status-based gates unchanged.
 *
 * It starts ``true`` for ``inflection_test`` (optimistic) and flips to whatever
 * the live editability response reports. On fetch failure it defaults to
 * ``true`` (editable) — the authoritative gate is still enforced server-side on
 * every mutation regardless of what the SPA shows.
 */

import { useEffect, useState } from 'react'

import type { FulfillmentStrategy } from '@/config/uiConfig'
import { logger } from '@/sentry.config'
import { getOrderEditability } from '@/utils/DatabaseOperations'

export interface UseIsOrderEditableInput {
  /** Resolved order fulfillment strategy. Only ``inflection_test`` fetches. */
  fulfillmentStrategy: FulfillmentStrategy
  /** IMDB order id; the fetch is skipped until this is known. */
  orderId?: string
  /** Audit caller string for the API request. */
  caller: string
}

export interface UseIsOrderEditableState {
  /**
   * ``true`` / ``false`` when the editability gate applies
   * (``inflection_test``); ``null`` when it does not (``rcigm``) so callers
   * keep their existing status-based editability. Defaults to ``true`` for
   * ``inflection_test`` while the fetch is pending.
   */
  editable: boolean | null
  /** Whether the ``inflection_test`` fetch is still in flight. */
  loading: boolean
}

export function useIsOrderEditable(input: UseIsOrderEditableInput): UseIsOrderEditableState {
  const { fulfillmentStrategy, orderId, caller } = input
  const applies = fulfillmentStrategy === 'inflection_test'

  // Optimistic default: inflection_test starts editable; rcigm is
  // not-applicable (null).
  const [editable, setEditable] = useState<boolean | null>(applies ? true : null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!applies) {
      setEditable(null)
      setLoading(false)
      return
    }
    if (!orderId) {
      // Keep the optimistic default until we have an order to ask about.
      setEditable(true)
      setLoading(false)
      return
    }

    let cancelled = false
    const controller = new AbortController()
    setLoading(true)

    const run = async () => {
      try {
        const resp = await getOrderEditability(orderId, caller, controller.signal)
        if (cancelled) return
        setEditable(resp.editable === true)
      } catch (error) {
        if (cancelled) return
        // Fail safe to editable on failure. The authoritative gate is enforced
        // server-side on every mutation regardless of what we show.
        logger.error('Failed to fetch order editability; defaulting to editable', {
          orderId,
          error: error instanceof Error ? error.message : String(error),
        })
        setEditable(true)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void run()

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [applies, orderId, caller])

  return { editable, loading }
}
